// Renders one docs page inside the center column: breadcrumb eyebrow, H1, lede,
// the page Body, a "Related" block, and a prev/next pager (from registry order).
// Sets document.title and upserts a <meta name="description"> for the page.
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ALL_PAGES, type DocPage } from './registry';

function useDocHead(title: string, description: string) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = `${title} — ClipRadar`;

    let meta = document.querySelector('meta[name="description"]');
    let created = false;
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
      created = true;
    }
    const prevDesc = meta.getAttribute('content');
    meta.setAttribute('content', description);

    return () => {
      document.title = prevTitle;
      if (created) meta?.parentNode?.removeChild(meta);
      else if (prevDesc !== null) meta?.setAttribute('content', prevDesc);
    };
  }, [title, description]);
}

function Related({ links }: { links: NonNullable<DocPage['related']> }) {
  return (
    <section style={{ marginTop: 40 }}>
      <span className="cr-label" style={{ marginBottom: 12, display: 'block' }}>Related</span>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
        {links.slice(0, 3).map((l) => (
          <Link key={l.to} to={l.to} className="crw-card-hover" style={{ display: 'block', padding: '13px 15px', borderRadius: 11, border: '1px solid var(--line-1)', background: 'var(--surface-card)', textDecoration: 'none', color: 'var(--fg-1)', fontSize: 13.5, fontWeight: 500 }}>
            {l.label}
            <span style={{ color: 'var(--signal-400)', marginLeft: 6 }}>→</span>
          </Link>
        ))}
      </div>
    </section>
  );
}

function Pager({ prev, next }: { prev?: DocPage; next?: DocPage }) {
  const cell = (dir: 'prev' | 'next', p?: DocPage) => {
    if (!p) return <span />;
    const to = p.slug === '' ? '/docs' : `/docs/${p.slug}`;
    return (
      <Link to={to} className="crw-card-hover" style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '14px 16px', borderRadius: 11, border: '1px solid var(--line-1)', background: 'var(--surface-card)', textDecoration: 'none', textAlign: dir === 'next' ? 'right' : 'left' }}>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--fg-4)' }}>{dir === 'prev' ? '← Previous' : 'Next →'}</span>
        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--fg-1)' }}>{p.title}</span>
      </Link>
    );
  };
  return (
    <nav style={{ marginTop: 32, paddingTop: 24, borderTop: '1px solid var(--line-1)', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))', gap: 12 }}>
      {cell('prev', prev)}
      {cell('next', next)}
    </nav>
  );
}

export function DocPageView({ page }: { page: DocPage }) {
  useDocHead(page.title, page.description);

  const idx = ALL_PAGES.indexOf(page);
  const prev = idx > 0 ? ALL_PAGES[idx - 1] : undefined;
  const next = idx >= 0 && idx < ALL_PAGES.length - 1 ? ALL_PAGES[idx + 1] : undefined;
  const Body = page.Body;

  return (
    <article className="crw-page-in" style={{ minWidth: 0 }}>
      <header style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 26 }}>
        <span className="cr-label" style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--signal-400)' }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--signal-400)', boxShadow: '0 0 8px var(--signal-glow)' }} />
          {page.eyebrow}
        </span>
        <h1 className="crw-h-grad" style={{ margin: 0, fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'clamp(28px, 3.4vw, 40px)', letterSpacing: 'var(--tracking-display)', lineHeight: 1.1, textWrap: 'balance' }}>
          {page.title}
        </h1>
        <p style={{ margin: 0, maxWidth: 620, fontSize: 15.5, lineHeight: 1.7, color: 'var(--fg-2)', textWrap: 'pretty' }}>{page.description}</p>
      </header>

      <Body />

      {page.related && page.related.length ? <Related links={page.related} /> : null}
      <Pager prev={prev} next={next} />
    </article>
  );
}
